import {
    type NewCarCompareActionsType,
    NEW_CAR_COMPARE_CHANGE_COLOUR,
    NEW_CAR_COMPARE_GET_ETA,
    NEW_CAR_COMPARE_LOAD_FINANCE,
    NEW_CAR_COMPARE_SET_AVAILABLE_GRADES,
    NEW_CAR_COMPARE_SET_CAR_DATA,
    NEW_CAR_COMPARE_SET_ETA,
    NEW_CAR_COMPARE_SET_FINANCE,
    NEW_CAR_COMPARE_SET_NC_COMPARE_CONTEXT,
    NEW_CAR_COMPARE_SET_NEW_ENGINE,
    NEW_CAR_COMPARE_SET_PARSED_CAR_DATA,
    NEW_CAR_COMPARE_SET_FILTERED_CAR_LIST,
} from "../actions/NewCarCompareActions";
import type {
    EngineWithSpecsType,
    EquipmentAvailabilityType,
    EquipmentSpecItemType,
    PacksAvailabilityType,
    SelectGradeType,
} from "../../utils/compareTypes";
import { defaultFinanceInfoObject } from "../../../../common-deprecated/constants";
import {
    NEW_CAR_COMPARE_ADD_CAR,
    NEW_CAR_COMPARE_CLEAR_ALL,
    NEW_CAR_COMPARE_REMOVE_CAR,
    NEW_CAR_COMPARE_SET_FINANCE_PRODUCT,
} from "../../../../common-deprecated/features/compare/redux/actions/NewCarCompareExternalDataActions";
import type {
    CarDataType,
    CompareMotorizationType,
    EquipmentSpecNameIdType,
    SpecItemType,
} from "../../../../common-deprecated/features/compare/utils/newCarCompareTypes";
import { NcCompareContext } from "../../../../common-deprecated/types/NewCarCompareTypes";
import type { FilteredCarType } from "../../../../common-deprecated/types/FilterApiType";

export type NewCarCompareReducerType = {
    ncCompareContext: NcCompareContext;
    available: SelectGradeType[];
    grades: CarDataType[];
    specs: EngineWithSpecsType[][];
    equipmentList: EquipmentSpecItemType[][];
    equipment: EquipmentAvailabilityType[][];
    specList: SpecItemType[][];
    packs: PacksAvailabilityType[][];
    packsList: EquipmentSpecNameIdType[][];
    selectedEngines: CompareMotorizationType[];
    parsedSpecs: SpecItemType[];
    parsedEquipment: EquipmentSpecItemType[];
    parsedPacks: EquipmentSpecNameIdType[];
    filteredCarList: FilteredCarType[];
};

export const initialState: NewCarCompareReducerType = {
    ncCompareContext: NcCompareContext.Original,
    available: [],
    grades: [],
    specs: [],
    equipment: [],
    equipmentList: [],
    specList: [],
    packs: [],
    packsList: [],
    selectedEngines: [],
    parsedSpecs: [],
    parsedEquipment: [],
    parsedPacks: [],
    filteredCarList: [],
};

const replaceObjectInArray = <T>(array: T[], newObject: T, index: number): T[] => {
    return [...array.slice(0, index), newObject, ...array.slice(index + 1)];
};

const newCarCompareReducer = (
    state: NewCarCompareReducerType = initialState,
    action: NewCarCompareActionsType,
): NewCarCompareReducerType => {
    switch (action.type) {
        case NEW_CAR_COMPARE_CHANGE_COLOUR: {
            const newCar = { ...state.grades[action.index] };
            newCar.colourCode = action.colourCode;
            return {
                ...state,
                grades: replaceObjectInArray(state.grades, newCar, action.index),
            };
        }

        case NEW_CAR_COMPARE_CLEAR_ALL:
        case NEW_CAR_COMPARE_SET_FINANCE_PRODUCT: {
            const { available, ncCompareContext, filteredCarList } = state;
            return { ...initialState, filteredCarList, ncCompareContext, available };
        }

        case NEW_CAR_COMPARE_SET_AVAILABLE_GRADES: {
            return { ...state, available: action.available || state.available };
        }

        case NEW_CAR_COMPARE_ADD_CAR: {
            return {
                ...state,
                // When adding two cars at once the data gets added to the redux store in an array at a given index.
                // If say the second car (index 1) gets it's data first from the api it will store iets specs, grades, equipment... in the
                // 1 index of the array. But in this case the array is empty so it will save it in the 0 index, when after this the first
                // car receives its data from the api it will overwrite the data saved in index 0 of these arrays.
                // For this we add mockData for the grades and selectedEngines (we know the name so we only store these so it shows on screen)
                // and an empty array placeholder for the other data. This will reserve a place in the array when the data returns from the api.
                grades: action.data ? replaceObjectInArray(state.grades, action.data, action.index) : state.grades,
                specs: action.data ? replaceObjectInArray(state.specs, [], action.index) : state.specs,
                specList: action.data ? replaceObjectInArray(state.specList, [], action.index) : state.specList,
                equipment: action.data ? replaceObjectInArray(state.equipment, [], action.index) : state.equipment,
                equipmentList: action.data
                    ? replaceObjectInArray(state.equipmentList, [], action.index)
                    : state.equipmentList,
                packs: action.data ? replaceObjectInArray(state.packs, [], action.index) : state.packs,
                packsList: action.data ? replaceObjectInArray(state.packsList, [], action.index) : state.packsList,
                // Add placeholder where the engine will be stored.
                selectedEngines: action.data
                    ? replaceObjectInArray(
                          state.selectedEngines,
                          {
                              engineId: "",
                              engineName: "",
                              transmissionId: "",
                              transmissionName: "",
                              wheeldriveId: "",
                              wheeldriveName: "",
                          },
                          action.index,
                      )
                    : state.selectedEngines,
            };
        }

        case NEW_CAR_COMPARE_SET_PARSED_CAR_DATA: {
            return {
                ...state,
                parsedSpecs: action.specs,
                parsedEquipment: action.equipment,
                parsedPacks: action.packs,
            };
        }

        case NEW_CAR_COMPARE_REMOVE_CAR: {
            const indexToRemove = action.index;
            return {
                ...state,
                grades: state.grades.filter((item, index) => index !== indexToRemove),
                equipmentList: state.equipmentList.filter((item, index) => index !== indexToRemove),
                specList: state.specList.filter((item, index) => index !== indexToRemove),
                specs: state.specs.filter((item, index) => index !== indexToRemove),
                packsList: state.packsList.filter((item, index) => index !== indexToRemove),
                packs: state.packs.filter((item, index) => index !== indexToRemove),
                equipment: state.equipment.filter((item, index) => index !== indexToRemove),
                selectedEngines: state.selectedEngines.filter((item, index) => index !== indexToRemove),
            };
        }

        case NEW_CAR_COMPARE_SET_CAR_DATA: {
            const newState: NewCarCompareReducerType = {
                ...state,
                grades: replaceObjectInArray(state.grades, action.data.carData, action.index),
                equipmentList: replaceObjectInArray(state.equipmentList, action.data.equipmentNamesData, action.index),
                specList: replaceObjectInArray(state.specList, action.data.specsNamesData, action.index),
                specs: replaceObjectInArray(state.specs, action.data.enginesWithSpecs, action.index),
                equipment: replaceObjectInArray(state.equipment, action.data.equipmentData, action.index),
                selectedEngines: replaceObjectInArray(state.selectedEngines, action.data.selectedEngine, action.index),
                packsList: replaceObjectInArray(state.packsList, action.data.packsNamesData, action.index),
                packs: replaceObjectInArray(state.packs, action.data.packsData, action.index),
            };
            return newState;
        }

        case NEW_CAR_COMPARE_SET_NEW_ENGINE: {
            const { motorization } = action;
            return {
                ...state,
                selectedEngines: state.selectedEngines.map((item, index) => {
                    if (index !== action.index) return item;
                    return { ...item, ...motorization };
                }),
            };
        }

        case NEW_CAR_COMPARE_GET_ETA: {
            const { index } = action;
            const car = state.grades[index];
            car.eta.loading = true;
            return { ...state, grades: replaceObjectInArray(state.grades, car, index) };
        }

        case NEW_CAR_COMPARE_SET_ETA: {
            const { etaDate, errorMessage } = action;
            const grade = state.grades[action.index];
            const newGrade = {
                ...grade,
                eta: { ...grade.eta, loading: false, date: etaDate, error: !!errorMessage, errorMessage },
            };
            return { ...state, grades: replaceObjectInArray(state.grades, newGrade, action.index) };
        }

        case NEW_CAR_COMPARE_LOAD_FINANCE: {
            const { index } = action;
            const grade = state.grades[index];
            const newGrade = {
                ...grade,
                finance: { ...grade.finance, loading: true, rate: 0, financeInfo: defaultFinanceInfoObject },
            };
            return { ...state, grades: replaceObjectInArray(state.grades, newGrade, index) };
        }

        case NEW_CAR_COMPARE_SET_FINANCE: {
            const { data, index } = action;
            const grade = state.grades[index];
            const newGrade = {
                ...grade,
                finance: { ...grade.finance, ...data },
            };
            return { ...state, grades: replaceObjectInArray(state.grades, newGrade, index) };
        }

        case NEW_CAR_COMPARE_SET_NC_COMPARE_CONTEXT: {
            const { ncCompareContext } = action;
            return { ...state, ncCompareContext };
        }

        case NEW_CAR_COMPARE_SET_FILTERED_CAR_LIST: {
            const { filteredCarList } = action;
            return { ...state, filteredCarList };
        }

        default:
            return state;
    }
};

export default newCarCompareReducer;
